import dayjs, { Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { Session } from './types';

dayjs.extend(utc);
dayjs.extend(timezone);

export const getTime = (): string => {
  const now = new Date();
  return new Intl.DateTimeFormat('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(now);
};

export const getDate = (): string => {
  const now = new Date();
  return new Intl.DateTimeFormat('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  }).format(now);
};

export const getUTCOffset = (): string => {
  const offset = -new Date().getTimezoneOffset() / 60;
  const sign = offset >= 0 ? '+' : '-';
  return `UTC${sign}${Math.abs(offset)}`;
};

export const getCustomTimezoneDisplay = (): string => {
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const city = tz.split('/')[1].replace('_', ' ');
  const utcOffset = getUTCOffset();
  return `${city} ${utcOffset}`;
};

// Get the start of the current week (Monday)
export const getStartOfWeek = (date: Date): Date => {
  const day = date.getDay(); // 0 (Sunday) to 6 (Saturday)
  const diff = day === 0 ? -6 : 1 - day; // Adjust for Monday as the start
  const monday = new Date(date);
  monday.setDate(monday.getDate() + diff);
  return monday;
};

// Generate an array of dates for the week (Monday to Sunday)
// export const getWeekDates = (startDate: Date): Date[] => {
//   return Array.from({ length: 7 }, (_, i) => {
//     const day = new Date(startDate);
//     day.setDate(startDate.getDate() + i);
//     return day;
//   });
// };
export const getWeekDates = (startDate: Dayjs): Dayjs[] => {
  return Array.from({ length: 7 }, (_, i) => startDate.add(i, 'day'));
};

export const formatWeekRange = (dates: Date[]): string => {
  if (dates.length === 0) return '';

  const options = { month: 'long', day: 'numeric' } as const;
  const startDate = new Intl.DateTimeFormat('en-US', options).format(dates[0]); // First day
  const endDate = new Intl.DateTimeFormat('en-US', options).format(dates[dates.length - 1]); // Last day

  return `${startDate} - ${endDate}`;
};

export const isToday = (date: Date): boolean => {
  const today = new Date();

  return (
    date.getFullYear() === today.getFullYear() &&
    date.getMonth() === today.getMonth() &&
    date.getDate() === today.getDate()
  );
};

export const isSameDay = (sessionDate: Date, tableDate: Date): boolean => {
  // Convert the UTC date to the user's local time
  const userLocalUTCOffset = tableDate.getTimezoneOffset(); // in minutes
  const localUtcNormalizedDate = new Date(sessionDate.getTime() - userLocalUTCOffset * 60 * 1000);

  return (
    localUtcNormalizedDate.getFullYear() === tableDate.getFullYear() &&
    localUtcNormalizedDate.getMonth() === tableDate.getMonth() &&
    localUtcNormalizedDate.getDate() === tableDate.getDate()
  );
};

export const isBeforeToday = (date: Date): boolean => {
  const today = new Date();

  // Clear time part from today's date
  today.setHours(0, 0, 0, 0);

  // Clear time part from the given date
  const givenDate = new Date(date);
  givenDate.setHours(0, 0, 0, 0);

  // Compare the two dates
  return givenDate < today;
};

export const formatDateToYYYYMMDD = (date: Date): string => {
  // Adjust the date to the user's local timezone
  const localDate = new Date(date.getTime() - date.getTimezoneOffset() * 60 * 1000);

  // Extract the components
  const year = localDate.getFullYear();
  const month = String(localDate.getMonth() + 1).padStart(2, '0'); // Months are zero-based
  const day = String(localDate.getDate()).padStart(2, '0');

  // Return in the desired format
  return `${year}-${month}-${day}`;
};

export function formatTimeRange(startTimeUTC: string, durationMinutes: number): string {
  try {
    // Parse the start time as UTC
    const startTime = dayjs.utc(startTimeUTC);

    // Check if the start time is valid
    if (!startTime.isValid()) {
      throw new Error(`Invalid startTimeUTC: ${startTimeUTC}`);
    }

    // Calculate the end time by adding the duration in minutes
    const endTime = startTime.add(durationMinutes, 'minutes');

    // Format the times in the user's local timezone
    const startLocal = startTime.local().format('HH:mm');
    const endLocal = endTime.local().format('HH:mm');

    // Return the formatted time range
    return `${startLocal} - ${endLocal}`;
  } catch (error) {
    console.error('Error in formatTimeRange:', error);
    return 'Invalid Date';
  }
}

interface Level {
  range: [number, number];
  level: string;
}

const levels: Level[] = [
  { range: [10, 29], level: 'A1' },
  { range: [30, 35], level: 'A2' },
  { range: [36, 42], level: 'A2+' },
  { range: [43, 50], level: 'B1' },
  { range: [51, 58], level: 'B1+' },
  { range: [59, 66], level: 'B2' },
  { range: [67, 75], level: 'B2+' },
  { range: [76, 84], level: 'C1' },
  { range: [85, 90], level: 'C2' },
];

export const gseRangeToCEFR = (min: number, max: number) => {
  const compatibleLevels: string[] = [];
  levels.forEach(({ range, level }) => {
    const [rangeMin, rangeMax] = range;
    if ((min <= rangeMax && max >= rangeMin) || (max >= rangeMin && min <= rangeMax)) {
      compatibleLevels.push(level);
    }
  });

  return compatibleLevels.join('/');
};
export function getIsJoinBtnActive(sessionDate: string): boolean {
  const now = dayjs(); // Current local time
  const classStart = dayjs.utc(sessionDate).local(); // Convert UTC to local time

  const fifteenMinutesBefore = classStart.subtract(15, 'minute');
  const fifteenMinutesAfter = classStart.add(15, 'minute');

  // Check if the current time is between 15 minutes before and 15 minutes after the class
  return now.isAfter(fifteenMinutesBefore) && now.isBefore(fifteenMinutesAfter);
}

export function getClassStatus(session: Session): 'Confirmed' | 'Pending' | 'Canceled' {
  const now = dayjs(); // Current time
  const classStart = dayjs(session.date); // Class start time
  const fifteenMinutesBefore = classStart.subtract(15, 'minute');

  if (session.enrollments > 0) {
    return 'Confirmed';
  }
  if (now.isBefore(fifteenMinutesBefore)) {
    return 'Pending';
  }
  return 'Canceled';
}

export const getContentEnv = () => {
  let params: Record<string, string> = {};
  if (typeof window !== 'undefined') {
    const urlSearchParams = new URLSearchParams(window.location.search);
    params = Object.fromEntries(urlSearchParams.entries());
  }
  return params.contentEnv || process.env.REACT_APP_CONTENT_ENV || 'prod';
};

export const tutoringApiUrl = () => {
  switch (getContentEnv()) {
    case 'prod':
      return 'https://api.prd2-tutoring.pearsonprd.tech';
    case 'stage':
      return 'https://api.tutoring-stg.pearsondev.tech';
    case 'dev':
      return 'https://api.tutoring-dev.pearsondev.tech';
    case 'seduc-prod':
      return 'https://api.tutoring-haven.on.wizard.com.br';
    case 'seduc-stg':
      return 'https://api.tutoring.stg.on.wizard.com.br';
    default:
      return 'https://api.tutoring-stg.pearsondev.tech';
  }
};

export const IesClientId = () => {
  switch (getContentEnv()) {
    case 'prod':
      return 'LIVTUT-Client';
    case 'stage':
      return 'LIVTUT-Client';
    case 'dev':
      return 'LIVTUT-Client';
    case 'seduc-prod':
      return 'WIZPTL-Client';
    case 'seduc-stg':
      return 'WIZPTL-Client';
    default:
      return 'LIVTUT-Client';
  }
};

export const tutorPortalUrl = () => {
  switch (getContentEnv()) {
    case 'prod':
      return 'https://teach.tutoring-prd.pearsonprd.tech/';
    case 'stage':
      return 'https://teach.tutoring-stg.pearsondev.tech/';
    case 'dev':
      return 'https://teach.tutoring-dev.pearsondev.tech/';
    case 'seduc-prod':
      return 'https://portal.tutoring-haven.on.wizard.com.br';
    case 'seduc-stg':
      return 'https://portal.tutoring.stg.on.wizard.com.br';
    default:
      return 'https://teach.tutoring-stg.pearsondev.tech/';
  }
};

export const IesUrl = () => {
  switch (getContentEnv()) {
    case 'prod':
      return `https://iam.pearson.com`;
    case 'stage':
      return `https://iam-stage.pearson.com`;
    case 'dev':
      return `https://iam-stage.pearson.com`;
    case 'seduc-prod':
      return `https://iam.pearson.com`;
    case 'seduc-stg':
      return `https://iam-stage.pearson.com`;
    default:
      return `https://iam-stage.pearson.com`;
  }
};

export const IesTutorPortalUrl = () => {
  switch (getContentEnv()) {
    case 'prod':
      return `${IesUrl()}/auth/oauth2/realms/pearson/authorize?service=AuthN&client_id=${IesClientId()}&&scope=openid&response_type=code&redirect_uri=`;
    case 'stage':
      return `${IesUrl()}/auth/oauth2/realms/pearson/authorize?service=AuthN&client_id=${IesClientId()}&&scope=openid&response_type=code&redirect_uri=`;
    case 'dev':
      return `${IesUrl()}/auth/oauth2/realms/pearson/authorize?service=AuthN&client_id=${IesClientId()}&&scope=openid&response_type=code&redirect_uri=`;
    case 'seduc-prod':
      return `${IesUrl()}/auth/oauth2/realms/pearson/authorize?service=AuthN&client_id=${IesClientId()}&&scope=openid&response_type=code&redirect_uri=`;
    case 'seduc-stg':
      return `${IesUrl()}/auth/oauth2/realms/pearson/authorize?service=AuthN&client_id=${IesClientId()}&scope=openid&response_type=code&redirect_uri=`;
    default:
      return `${IesUrl()}/auth/oauth2/realms/pearson/authorize?service=AuthN&client_id=${IesClientId()}&&scope=openid&response_type=code&redirect_uri=`;
  }
};

export const sessionScriptSrcByEnv = () => {
  switch (getContentEnv()) {
    case 'prod':
      return 'https://login.pearson.com/v1/piapi/login/js/v2/session.js';
    case 'stage':
      return 'https://login-stg.pearson.com/v1/piapi-int/login/js/v2/session.js';
    case 'dev':
      return 'https://login-stg.pearson.com/v1/piapi-int/login/js/v2/session.js';
    case 'seduc-prod':
      return 'https://login.pearson.com/v1/piapi/login/js/v2/session.js';
    case 'seduc-stg':
      return 'https://login-stg.pearson.com/v1/piapi-int/login/js/v2/session.js';
    default:
      return 'https://login-stg.pearson.com/v1/piapi-int/login/js/v2/session.js';
  }
};

export const getNextMonday = (today: dayjs.Dayjs) => {
  const dayOfWeek = today.day();
  // TODO: activate when startOfWeekDay is dynamic:
  // if (startOfWeekDay === 0) {
  //   // Calculate days until next Sunday
  //   const daysUntilNextSunday = (7 - dayOfWeek) % 7 || 7;
  //   return today.add(daysUntilNextSunday, 'day');
  // } else {
  // Calculate days until next Monday
  const daysUntilNextMonday = (8 - dayOfWeek) % 7 || 7;
  return today.add(daysUntilNextMonday, 'day');
  // }
};

export const getCurrWeekMonday = (date: dayjs.Dayjs): dayjs.Dayjs => {
  // Get the current day of the week (0 = Sunday, 1 = Monday, ..., 6 = Saturday)
  const currentDayOfWeek = date.day();

  // Calculate the number of days to subtract to reach the Monday of this week
  const daysToSubtract = currentDayOfWeek === 0 ? 6 : currentDayOfWeek - 1;

  // Return the Monday of the current week
  return date.subtract(daysToSubtract, 'day');
};

export const getPreviousWeekMonday = (date: dayjs.Dayjs): dayjs.Dayjs => {
  // Get the current day of the week (0 = Sunday, 1 = Monday, ..., 6 = Saturday)
  const currentDayOfWeek = date.day();

  // Calculate the number of days to subtract to get to the Monday of the previous week
  // If it's Monday (day 1), subtract 7 days; otherwise, subtract (currentDayOfWeek + 6)
  const daysToSubtract = currentDayOfWeek === 1 ? 7 : currentDayOfWeek + 6;

  // Return the Monday of the previous week
  return date.subtract(daysToSubtract, 'day').day(1); // Ensure it's a Monday
};
